<script>
  import { _, contract } from "../../stores/index";
  import { onMount, getContext } from "svelte";
  import {
    AppsRepository,
    ContractsRepository,
  } from "../../repositories/index";
  import { parseUrl, setUrl } from "../../utils/index";
  import { ConfirmModal, Layout } from "../../components/index";
  import { Link } from "svelte-routing";
  import {
    SimpleDataTable,
    DataCell,
    Flex,
    FlexItem,
    Loader,
    Select,
    SelectItem,
    TextField,
    Button,
  } from "@gl/design-system";
  import {
    faSort,
    faSortDown,
    faSortUp,
  } from "@fortawesome/pro-solid-svg-icons";
  import { format } from "date-fns";
  const { isAuthorized } = getContext("isAuthorized");

  let apps = [];
  let loading = {};

  let selectedApp;

  let availableTypes = [
    "gl-access-manager",
    "gl-compositor",
    "gl-graphics-manager",
    "gl-channel-manager",
    "gl-content-manager",
  ];

  let search = { type: "all", name: "" };
  let searched;
  let order = {
    asc: true,
  };

  const handleDelete = async () => {
    try {
      loading.delete = true;
      await AppsRepository.deleteApplication(selectedApp.id);
    } finally {
      fetchApps();
      loading.delete = true;
    }
  };

  const fetchApps = async () => {
    try {
      loading.apps = true;
      const result = await ContractsRepository.getContractApps($contract?.id);
      apps = result.list;
      searched = apps;
    } finally {
      loading.apps = false;
      handleSearch();
    }
  };

  const handleSearch = () => {
    searched = apps
      .filter((app) => app.type === search.type || search.type === "all")
      .filter((app) =>
        app.name.toUpperCase().includes(search.name.trim().toUpperCase()),
      );
    if (order.by) {
      sort(order.by, false);
    } else {
      setUrl({ ...search, orderBy: order.by, orderAsc: order.asc });
    }
  };

  const handleReset = () => {
    search = { type: "all", name: "" };
    order = { asc: true };
    handleSearch();
  };

  function sort(field = null, toggleAsc = true) {
    if (toggleAsc) {
      order.asc = !order.asc;
    }
    order.by = field;

    const cmpA = order.asc ? -1 : 1;
    const cmpB = order.asc ? 1 : -1;
    searched = searched.sort((a, b) => {
      const A =
        field === "modified"
          ? a.metadata.modifiedAt
          : a[order.by].toUpperCase();
      const B =
        field === "modified"
          ? b.metadata.modifiedAt
          : b[order.by].toUpperCase();
      return A === B ? 0 : A < B ? cmpA : cmpB;
    });
    order.icon = order.asc ? "ArrowDown" : "ArrowUp";
    setUrl({ ...search, orderBy: order.by, orderAsc: order.asc });
  }

  onMount(() => {
    const query = parseUrl(window.location.search);
    search = { ...search, type: query.type || "all", name: query.name || "" };
    if (query.orderBy) {
      order.by = query.orderBy;
      order.asc = query.orderAsc === "true";
    }
    fetchApps();
  });
</script>

<svelte:head>
  <title>
    {$_("common.apps")} | {$_("access-manager")}
  </title>
</svelte:head>

<Layout selected="applications">
  {#if loading.apps}
    <Loader />
  {:else}
    <h3 class="level-3 gl-title">{$_("common.apps")}</h3>

    <p class="mb-5">
      {$_("apps.total", {
        values: { total: apps.length },
      })}
    </p>
    <Flex class="mb-5 align-items-center">
      <FlexItem class="mr-4">
        <Select bind:value={search.type} id="group">
          <SelectItem
            label={`${$_("common.type")}: ${$_("common.all")}`}
            value="all"
          />
          {#each availableTypes as type}
            <SelectItem label={`${$_("common.type")}: ${type}`} value={type} />
          {/each}
        </Select>
      </FlexItem>

      <FlexItem style="width:300px" class="mr-4">
        <TextField
          variant="Search"
          bind:value={search.name}
          on:keydown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />
      </FlexItem>

      <FlexItem class="mr-4">
        <Button on:click={handleSearch}>
          {$_("common.search")}
        </Button>
      </FlexItem>

      <FlexItem>
        <Button outlined on:click={handleReset}>
          {$_("common.reset")}
        </Button>
      </FlexItem>
    </Flex>

    <div class="table-container">
      <SimpleDataTable
        let:index
        let:row
        data={searched || apps}
        fullWidth
        selectable={false}
        loading={loading.apps}
        variant="separate"
        stickyHead
      >
        <slot name="empty" slot="empty">
          <div class="gl-message" style="text-align: center">
            {$_("common.no.display")}
          </div>
        </slot>
        <DataCell
          {index}
          id={`${row.id}-id`}
          header="ID"
          headerIcon={order.by === "id" && order.asc
            ? faSortUp
            : order.by === "id" && !order.asc
              ? faSortDown
              : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("id")}
          maxWidth={300}
        >
          {#if $isAuthorized("user_read")}
            <Link
              to={`access-manager/contracts/${$contract?.id}/applications/${row.id}/members`}
              >{row.id}</Link
            >
          {:else}
            {row.id}
          {/if}
        </DataCell>

        <DataCell
          {index}
          header={$_("common.name")}
          id={`${row.id}-name`}
          maxWidth={300}
        >
          {row.name}
        </DataCell>

        <DataCell
          {index}
          header={$_("common.type")}
          id={`${row.id}-type`}
          maxWidth={300}
        >
          {row.type ?? "-"}
        </DataCell>

        <DataCell
          {index}
          header={$_("apps.policy.definition")}
          id={`${row.id}-policy`}
        >
          {row.policyDefinition}
        </DataCell>

        <DataCell
          {index}
          id={`${row.id}-modified`}
          header={$_("common.last.modified")}
          headerIcon={order.by === "modified" && order.asc
            ? faSortUp
            : order.by === "modified" && !order.asc
              ? faSortDown
              : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("modified")}
        >
          {row.metadata?.modifiedAt
            ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss")
            : "-"}
          {#if row.metadata?.modifiedBy}
            <p class="text-light" style="font-size:14px">
              {$_("common.by")}
              {row.metadata.modifiedBy}
            </p>
          {/if}
        </DataCell>
      </SimpleDataTable>
    </div>
  {/if}
</Layout>
